import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { LoadRequest } from "src/app/support/load-request";
import { UserNotify } from "src/app/support/notify-decorator";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import {
  Ticket,
  TicketCreate,
  TicketLight,
  TicketLoad,
  TicketStatus
} from "../models/ticket";
import { sntTranslate } from "src/app/support/translate";
import { SoapTemplate } from "src/app/backend/models/soap-template";
import { first } from "lodash-es";
import { omitUndefinedAndNullProperties } from "src/app/support/object";

@Injectable()
export class TicketBackend {
  private readonly http = inject(HttpClient);

  loadPublic(id: string): Observable<Ticket> {
    return this.http.get<Ticket>(
      `${environment.snoots.api.healthcare}/public/tickets/${id}`
    );
  }

  list(request: LoadRequest): Observable<PaginatedResult<Ticket>> {
    return this.http.get<PaginatedResult<Ticket>>(
      `${environment.snoots.api.healthcare}/tickets`
    );
  }

  search(query: string): Observable<PaginatedResult<Ticket>> {
    return this.http.get<PaginatedResult<Ticket>>(
      `${environment.snoots.api.healthcare}/tickets`,
      {
        params: {
          number: query
        }
      }
    );
  }

  listByStatus(
    status: string,
    filters?: { checkedOutAt?: string }
  ): Observable<Ticket[]> {
    return this.http
      .get<PaginatedResult<Ticket>>(
        `${environment.snoots.api.healthcare}/tickets`,
        {
          params: omitUndefinedAndNullProperties({
            status,
            checkedOutAt: filters?.checkedOutAt
          })
        }
      )
      .pipe(map((x) => x.docs));
  }

  listByStatusAndStaff(
    status: TicketStatus,
    staff: string
  ): Observable<Ticket[]> {
    return this.http
      .get<PaginatedResult<Ticket>>(
        `${environment.snoots.api.healthcare}/tickets`,
        {
          params: {
            status,
            staffId: staff
          }
        }
      )
      .pipe(map((x) => x.docs));
  }

  loadFirstNotCompletedByPatient(
    patientId: string
  ): Observable<Ticket | undefined> {
    return this.loadByPatient(patientId).pipe(
      map((response) =>
        first(
          response.docs.filter(
            (doc) =>
              doc.status === "checked-in" ||
              doc.status === "in-progress" ||
              doc.status === "completed"
          )
        )
      )
    );
  }

  @UserNotify({
    successMessage: marker("Ticket creato"),
    errorMessage: marker("Impossibile creare il ticket")
  })
  create(payload: TicketCreate): Observable<Ticket> {
    return this.createSilently(payload);
  }

  createSilently(payload: TicketCreate) {
    return this.http.post<Ticket>(
      `${environment.snoots.api.healthcare}/tickets`,
      payload
    );
  }

  load(request: TicketLoad): Observable<Ticket> {
    return this.http.get<Ticket>(
      `${environment.snoots.api.healthcare}/tickets/${request.uuid}`
    );
  }

  loadLight(request: TicketLoad): Observable<TicketLight> {
    return this.http.get<TicketLight>(
      `${environment.snoots.api.healthcare}/tickets/${request.uuid}`,
      {
        params: { light: true }
      }
    );
  }

  loadByCustomerInProgress(customerId: string): Observable<Ticket | undefined> {
    return this.http
      .get<PaginatedResult<Ticket>>(
        `${environment.snoots.api.healthcare}/tickets`,
        {
          params: {
            customerId
          }
        }
      )
      .pipe(
        map((tickets) =>
          tickets.docs.filter(
            (ticket) =>
              ticket.status === "checked-in" ||
              ticket.status === "in-progress" ||
              ticket.status === "completed"
          )
        ),
        map((tickets) => first(tickets))
      );
  }

  loadByPatient(patientId: string): Observable<PaginatedResult<Ticket>> {
    return this.http.get<PaginatedResult<Ticket>>(
      `${environment.snoots.api.healthcare}/patients/${patientId}/tickets`
    );
  }

  loadByPatientInProgress(patientId: string): Observable<Ticket | undefined> {
    return this.loadByPatient(patientId).pipe(
      map((tickets: PaginatedResult<Ticket>) =>
        tickets.docs.filter(
          (ticket) =>
            ticket.status === "checked-in" ||
            ticket.status === "in-progress" ||
            ticket.status === "completed"
        )
      ),
      map((tickets) => first(tickets))
    );
  }

  @UserNotify({
    successMessage: marker("Ticket eliminato"),
    errorMessage: marker("Impossibile eliminare il ticket")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.healthcare}/tickets/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Ticket aggiornato"),
    errorMessage: marker("Impossibile aggiornare il ticket")
  })
  update(id: string, request: Partial<Ticket>): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/tickets/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: (ticket: Ticket, soapTemplate?: SoapTemplate) =>
      soapTemplate
        ? `${soapTemplate.name} iniziata`
        : sntTranslate(marker("Visita iniziata")),
    errorMessage: marker("Errore")
  })
  open(ticket: Ticket): Observable<void> {
    return this.openSilently(ticket.id);
  }

  openSilently(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.healthcare}/tickets/${id}/start`,
      {}
    );
  }

  @UserNotify({
    successMessage: (ticket: Ticket) =>
      `${sntTranslate(marker("Visita"))} ${ticket.number} ${sntTranslate(
        marker("completata")
      )}`,
    errorMessage: marker("Impossibile completare il ticket")
  })
  complete(ticket: Ticket): Observable<void> {
    return this.completeSilently(ticket);
  }

  completeSilently(ticket: { id: string }): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.healthcare}/tickets/${ticket.id}/complete`,
      {}
    );
  }

  @UserNotify({
    successMessage: (ticket: Ticket) =>
      `${sntTranslate(marker("Accettazione"))} ${ticket.number} ${sntTranslate(
        marker("completata")
      )}`,
    errorMessage: marker("Impossibile completare il check out")
  })
  checkout(ticket: { id: string }): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.healthcare}/tickets/${ticket.id}/checkout`,
      {}
    );
  }

  @UserNotify({
    successMessage: marker("Ticket aggiornato"),
    errorMessage: marker("Impossibile aggiornare il ticket")
  })
  checkedIn(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.healthcare}/tickets/${id}/checkin`,
      {}
    );
  }
}
